import React, { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  CardActions,
  Button,
  IconButton,
  useTheme,
  Tooltip,
} from '@mui/material'
import { useAppSnackbar } from '../../hooks/useAppSnackbar'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import LoopIcon from '@mui/icons-material/Loop'
import EditIcon from '@mui/icons-material/Edit'
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay'
import SaveIcon from '@mui/icons-material/Save'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { Playlist } from '../../swagger'
import { playlistApi } from '../../api/swagger'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import useLoadingContext from '../../hooks/useLoadingContext'
import { useNavigate, useLocation } from 'react-router-dom'
import { usePlaylistLoop } from '../../hooks/usePlaylistLoop'
import { truncateText } from '../../utils/stringUtility'

interface PlaylistActionCardProps {
  playlist: Playlist
  updatePlaylistNameContext: (value: string) => void
  itemCount: number
  firstPlaylistItemKey?: number
  isShared?: boolean
}

const PlaylistActionCard: React.FC<PlaylistActionCardProps> = ({
  playlist,
  isShared,
  updatePlaylistNameContext,
  firstPlaylistItemKey,
  itemCount,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { handleError, handleMessage } = useAppSnackbar()
  const { isLoopEnabled, toggleLoop } = usePlaylistLoop()

  const {
    Playlist: { updatePlaylistName: updatePlaylistNameLoadingId },
  } = useLoadingIds()

  const [isEdit, setIsEdit] = useState(false)
  const [playlistName, setPlaylistName] = useState(playlist.name)

  const createdAt = playlist.createdDate ?? new Date()

  const CreatedLabel = t(
    'PlaylistActionCard.Card.Label',
    'Created: {{createdDate}}',
    {
      createdDate: format(createdAt, 'MM/dd/yyyy'),
    }
  )

  const itemsCount = t('{{itemCount}} items', {
    itemCount,
  })

  const defaultUpdatePlaylistNameErrorMessage = t(
    'PlaylistActionCard.UpdatePlaylistName.Error',
    'An error occurred while trying to update the playlist name.'
  )

  const updatePlaylistName = async () => {
    try {
      await playlistApi.updatePlaylistName({
        playlistKey: playlist.playlistKey,
        body: {
          name: playlistName,
        },
      })

      updatePlaylistNameContext(playlistName)
    } catch (error) {
      await handleError(error, defaultUpdatePlaylistNameErrorMessage)
      setPlaylistName(playlist.name)
    }
  }

  const { triggerFetch: triggerUpdatePlaylistName } = useLoadingContext({
    asyncFunction: updatePlaylistName,
    loadingId: updatePlaylistNameLoadingId,
  })

  const handleAction = () => {
    if (isEdit) {
      if (!playlistName.trim()) {
        handleMessage(
          t(
            'PlaylistActionCard.Error.NameEmpty',
            'The playlist name cannot be empty.'
          ),
          'error'
        )
        return
      }

      if (playlistName !== playlist.name) {
        triggerUpdatePlaylistName()
      }
    }

    setIsEdit((prev) => !prev)
  }

  const goToPlayAll = () => {
    navigate(
      `${pathname.replace('-details', '/content')}/${firstPlaylistItemKey}`
    )
  }

  useEffect(() => {
    setPlaylistName(playlist.name)
  }, [playlist.name])

  return (
    <Box>
      <Card elevation={4}>
        <CardContent>
          <Box
            display="flex"
            bgcolor={theme.palette.customBackground.greenBackground}
            alignItems="center"
            height={theme.spacing(15.7)}
            sx={{
              height: '17rem',
              width: '100%',
              minWidth: '10rem',
              justifyContent: 'center',
              borderRadius: theme.spacing(1),
            }}
          >
            <PlaylistPlayIcon
              sx={{
                height: '15rem',
                width: '15rem',
                minWidth: '10rem',
                color: 'white',
                background: theme.palette.common.black,
              }}
            />
          </Box>
          <Box
            mt={theme.spacing(2)}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            {!isEdit ? (
              <Typography
                variant="h5"
                sx={{
                  fontSize: theme.spacing(3),
                  textTransform: 'uppercase',
                  fontWeight: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                {t('PlaylistActionCard.Card.Title', '{{playlistName}}', {
                  playlistName: truncateText(playlistName, 80),
                })}
              </Typography>
            ) : (
              <TextField
                InputLabelProps={{
                  style: { fontSize: theme.spacing(2) },
                }}
                sx={{
                  '& .MuiOutlinedInput-root.Mui-disabled fieldset': {
                    border: 'none',
                  },
                  '& .MuiOutlinedInput-root.Mui-disabled': {
                    backgroundColor: 'transparent',
                  },
                }}
                inputProps={{
                  style: {
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    fontSize: theme.spacing(3),
                    ...(!isEdit ? { padding: 0 } : {}),
                  },
                }}
                name="name"
                value={playlistName}
                disabled={!isEdit}
                onChange={(event) => {
                  setPlaylistName(event.target.value)
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    handleAction()
                  }
                }}
              />
            )}

            <Typography
              sx={{ textTransform: 'uppercase' }}
              lineHeight={2}
              display="flex"
              variant="subtitle1"
            >
              {CreatedLabel}
            </Typography>
            <Typography
              sx={{ textTransform: 'uppercase', fontSize: theme.spacing(2.3) }}
              lineHeight={2}
              display="flex"
              variant="subtitle1"
            >
              {itemsCount}
            </Typography>
          </Box>
        </CardContent>
        <CardActions
          sx={{
            margin: theme.spacing(2, 2),
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Button
            color="inherit"
            variant="contained"
            startIcon={<PlayArrowIcon />}
            size="small"
            onClick={goToPlayAll}
            sx={{
              '&:hover': {
                boxShadow: 'none',
              },
            }}
          >
            {t('PlaylistActionCard.Card.Button', 'Play All')}
          </Button>
          <Box>
            <Tooltip
              title={t(
                'Playlist.PlaylistActionCard.Tooltip.LoopAll',
                'Loop All'
              )}
            >
              <IconButton onClick={() => toggleLoop()}>
                <LoopIcon
                  sx={{
                    boxShadow: isLoopEnabled
                      ? '0 0 6px 2px rgba(0, 0, 0, 0.12)'
                      : 'none',
                    borderRadius: '50%',
                    transition: 'box-shadow 0.3s ease, color 0.3s ease',
                    display: 'inline-block',
                  }}
                  fontSize="large"
                  color={isLoopEnabled ? 'success' : 'primary'}
                />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                isShared
                  ? t(
                      'Playlist.PlaylistActionCard.Tooltip.RenameSharedPlaylist',
                      'Cannot rename shared playlist'
                    )
                  : isEdit
                  ? t(
                      'Playlist.PlaylistActionCard.Tooltip.SaveChanges',
                      'Save changes'
                    )
                  : t(
                      'Playlist.PlaylistActionCard.Tooltip.RenamePlaylist',
                      'Rename Playlist'
                    )
              }
            >
              <span>
                <IconButton
                  onClick={handleAction}
                  aria-label={isEdit ? 'Save' : 'Edit'}
                  data-testid={isEdit ? 'SaveButton' : 'EditButton'}
                  disabled={isShared}
                >
                  {isEdit ? (
                    <SaveIcon fontSize="large" />
                  ) : (
                    <EditIcon fontSize="large" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </CardActions>
      </Card>
    </Box>
  )
}

export default PlaylistActionCard
